import React, { FunctionComponent, useRef, useState, useEffect } from 'react'
import { PaymentType } from '@afterpay/types'
import { Heading, Loader, Radio, Text, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import {
  setSelectedPaymentType,
  useAppDispatch,
  useCheckoutState,
  useConsumerLendingState,
  useExperiment,
  useIsAfterpayFinanceFeeEligible,
  usePBIRejected,
  usePaymentOptionsError,
  usePreselectedPaymentType,
  useSelectedPaymentType,
} from '../../../state'
import { PaymentOptionsStatus } from '../../../model'
import type { PaymentOption } from '../../../model'
import clsx from 'clsx'
import { ConsumerLendingTagline } from '../../ConsumerLendingTagline'
import { ConsumerLendingRadioLabel } from '../../PaymentTypes/PaymentTypesRadioGroup/ConsumerLendingRadioLabel'
import { PayInFourRadioLabel } from '../../PaymentTypes/PaymentTypesRadioGroup/PayInFourRadioLabel'
import { PCLTooltip } from '../../PaymentTypes/PaymentTypesRadioGroup/PCLTooltip'
import { useCheckoutSizes } from '../../../utils/responsive'
import { useTranslation } from '../../../utils/convergence'
import { PaymentOptionsRadioGroup } from '../PaymentOptionsRadioGroup'
import paymentTypesStyles from '../../PaymentTypes/PaymentTypesRadioGroup/style.module.css'
import paymentOptionsStyles from '../style.module.css'

export interface PaymentOptionsExpandableRadioGroupProps {
  lock: boolean
  selectedPaymentOptionLocal?: PaymentOption
  setSelectedPaymentOptionLocal: (option: PaymentOption) => void
}

/**
 * Expandable payment type selection (PCL vs PBI) with data-driven middle section:
 * when PCL is selected, shows inline loader while fetching or PaymentOptionsRadioGroup when fetched.
 * Duplicates the type-selection Radio.Group structure from PaymentTypesRadioGroup; no slot props.
 * Used by PaymentOptionsExpandable (ExpandableLightweightPaymentPlans experiment).
 */
export const PaymentOptionsExpandableRadioGroup: FunctionComponent<PaymentOptionsExpandableRadioGroupProps> = ({
  lock,
  selectedPaymentOptionLocal,
  setSelectedPaymentOptionLocal,
}) => {
  const { t } = useTranslation()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const selectedPaymentType = useSelectedPaymentType()
  const { started } = useCheckoutState()
  const installmentsCount = started?.paymentSchedules?.length ?? 4
  const dispatch = useAppDispatch()
  const { extraSmallTextSize, textSize } = useCheckoutSizes()
  const hasPBIRejected = usePBIRejected()
  const paymentOptionsError = usePaymentOptionsError()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const buttonRef = useRef(null)
  const id = 'payInMoreAPR-help-tooltip'
  const [showTooltip, setShowTooltip] = useState(false)
  const [hasClickedOutside, setHasClickedOutside] = useState<boolean>(false)
  const preselectedPaymentType = usePreselectedPaymentType()
  const { paymentOptionsStatus, paymentOptions } = useConsumerLendingState()

  const payInFourHeadingStyles = clsx(paymentTypesStyles.payInFourHeading, hasPBIRejected && paymentTypesStyles.PBIRejectedGray)
  const { active: isDynamicAprLabelActive } = useExperiment('ConsumerLendingEnableDynamicAprLabel')

  const showPCLLoading = selectedPaymentType === PaymentType.PCL && paymentOptionsStatus < PaymentOptionsStatus.Fetched
  const showPlans = paymentOptionsStatus === PaymentOptionsStatus.Fetched && paymentOptions.length > 0
  const isPCLOrPBI = (t: typeof selectedPaymentType): t is PaymentType => t === PaymentType.PCL || t === PaymentType.PBI
  const hasSelectedType = isPCLOrPBI(selectedPaymentType)
  const selectedType = hasSelectedType ? selectedPaymentType : undefined

  const clickPaymentTypesGroup = (e): void => {
    const element = e.currentTarget as HTMLInputElement
    dispatch(setSelectedPaymentType(element.value as PaymentType))
  }

  useEffect(() => {
    if (preselectedPaymentType) {
      dispatch(setSelectedPaymentType(preselectedPaymentType))
    }
  }, [preselectedPaymentType])

  if (hasSelectedType && selectedType !== undefined) {
    const payIn4Section = (
      <Radio.Group id='payment-types-pay-in-4' name='payment-types' value={selectedType} testNameSpace='payment-types' onClick={clickPaymentTypesGroup}>
        <div className={payInFourHeadingStyles}>
          <Heading size={textSize}>
            {t('consumerLending:payInFourTagline', {
              // eslint-disable-next-line @afterpay/i18n-no-dynamic-context
              context: isAFF ? 'aff' : undefined,
              interval: installmentsCount,
            })}
          </Heading>
        </div>
        <Radio.Button
          value={PaymentType.PBI}
          selectedBorder={selectedType === PaymentType.PBI && !isArcadeDesignLanguage}
          defaultChecked={selectedType === PaymentType.PBI}
          labelPlacement='Top'
          kind='Option'
          disabled={hasPBIRejected}
          roundedBorder
          reversed
          label={() => <PayInFourRadioLabel />}
        />
      </Radio.Group>
    )

    return (
      <div data-testid='payment-options-expandable-selected'>
        {selectedType === PaymentType.PCL && (
          <>
            {showPCLLoading && (
              <>
                <div className={paymentTypesStyles.payInFourHeading}>
                  <Heading size={textSize}>{t('consumerLending:payInMoreTagline')}</Heading>
                </div>
                <div className={paymentOptionsStyles.inlineLoaderWrapper}>
                  <Loader />
                </div>
              </>
            )}

            {showPlans && (
              <PaymentOptionsRadioGroup
                lock={lock}
                selectedPaymentOptionLocal={selectedPaymentOptionLocal}
                setSelectedPaymentOptionLocal={setSelectedPaymentOptionLocal}
              />
            )}
            {showPCLLoading && payIn4Section}
          </>
        )}

        {selectedType === PaymentType.PBI && (
          <div className={paymentTypesStyles.payInFourHeading}>
            <Heading size={textSize}>
              {t('consumerLending:payInFourTagline', {
                // eslint-disable-next-line @afterpay/i18n-no-dynamic-context
                context: isAFF ? 'aff' : '',
                interval: installmentsCount,
              })}
            </Heading>
          </div>
        )}
      </div>
    )
  }

  return (
    <Radio.Group id='payment-types' name='payment-types' value={selectedPaymentType?.toString()} testNameSpace='payment-types' onClick={clickPaymentTypesGroup}>
      <div className={paymentTypesStyles.payInMoreTagline}>
        <ConsumerLendingTagline />
      </div>

      <div className={paymentTypesStyles.eligibilityHeading}>
        <Text size='XS' color='Gray50'>
          {t('consumerLending:paymentOptions.checkYourEligibility')}
        </Text>
      </div>

      <Radio.Button
        value={PaymentType.PCL}
        defaultChecked={selectedPaymentType === PaymentType.PCL}
        selectedBorder={selectedPaymentType === PaymentType.PCL && !isArcadeDesignLanguage}
        labelPlacement='Top'
        kind='Option'
        labelWidth='Full'
        roundedBorder
        reversed
        disabled={Boolean(paymentOptionsError?.error)}
        label={() => (
          <ConsumerLendingRadioLabel
            buttonRef={buttonRef}
            showTooltip={showTooltip}
            setShowTooltip={setShowTooltip}
            hasClickedOutside={hasClickedOutside}
            setHasClickedOutside={setHasClickedOutside}
            payInMoreAPRAriaDescribedBy={id}
          />
        )}
        id='payment-types-2'
        testNameSpace='payment-types'
        name='payment-types'
      />

      {showTooltip && <PCLTooltip tooltipId={id} buttonRef={buttonRef} setHasClickedOutside={setHasClickedOutside} onClose={() => setShowTooltip(false)} />}

      <div className={paymentTypesStyles.payInMoreSubjectToChange}>
        <Text color='Gray50' size={extraSmallTextSize}>
          {t('consumerLending:paymentOptions.subjectToChange', {
            // eslint-disable-next-line @afterpay/i18n-no-dynamic-context
            context: isDynamicAprLabelActive ? 'dynamic' : '',
          })}
        </Text>
      </div>

      <div className={payInFourHeadingStyles}>
        <Heading size={textSize}>
          {t('consumerLending:payInFourTagline', {
            // eslint-disable-next-line @afterpay/i18n-no-dynamic-context
            context: isAFF ? 'aff' : '',
            interval: installmentsCount,
          })}
        </Heading>
      </div>
      <Radio.Button
        value={PaymentType.PBI}
        selectedBorder={selectedPaymentType === PaymentType.PBI && !isArcadeDesignLanguage}
        defaultChecked={selectedPaymentType === PaymentType.PBI}
        labelPlacement='Top'
        kind='Option'
        disabled={hasPBIRejected}
        roundedBorder
        reversed
        label={() => <PayInFourRadioLabel />}
      />
    </Radio.Group>
  )
}
